/**
 * Organization Details Page
 *
 * Displays detailed information about a specific organization.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Building2,
  Calendar,
  Users,
  CreditCard,
  ArrowLeft,
  Hash,
  Layers,
  CheckCircle2,
  XCircle,
  Shield,
  RefreshCw,
  ArrowUpCircle,
  History,
} from 'lucide-react';

import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Loader,
  AuditTimeline,
} from '@/components/common';
import { organizationService, auditService } from '@/api/services';
import type { OrganizationDetails, OrganizationPlan } from '@/api/types';
import { formatDate } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';
import { useCan } from '@/hooks/usePermissions';

// ============================================
// Config Flag Component
// ============================================

function ConfigFlag({ label, enabled }: { label: string; enabled?: boolean | null }) {
  const isEnabled = !!enabled;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
        isEnabled
          ? 'bg-success/10 text-success'
          : 'bg-secondary-100 text-foreground-muted dark:bg-secondary-800/50 dark:text-foreground-dark-muted'
      }`}
    >
      {isEnabled ? (
        <CheckCircle2 className="h-3.5 w-3.5" />
      ) : (
        <XCircle className="h-3.5 w-3.5" />
      )}
      {label}
    </span>
  );
}

// ============================================
// Page Component
// ============================================

export function OrganizationDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManagePlan = useCan('orgPlan.plan.manage');

  // State
  const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
  const [plan, setPlan] = useState<OrganizationPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);

  const auditId = plan?.auditId ?? plan?.id;

  const fetchAuditLogs = useCallback(
    (page: number, pageSize: number) => {
      return auditService.getAuditLogs(auditId!, page, pageSize);
    },
    [auditId]
  );

  // Fetch data
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [orgData, planData] = await Promise.all([
          organizationService.getOrganizationById(id),
          organizationService.getOrganizationPlan(id),
        ]);
        setOrganization(orgData);
        setPlan(planData[0] || null);
      } catch (error) {
        console.error('Failed to fetch organization details:', error);
        toast.error('Failed to load organization details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading organization details..." />
      </div>
    );
  }

  // Not found state
  if (!organization) {
    return (
      <div className="text-center py-16">
        <Building2 className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
        <h2 className="mt-4 text-lg font-semibold text-foreground dark:text-foreground-dark">
          Organization not found
        </h2>
        <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
          The organization you are looking for does not exist.
        </p>
        <Button className="mt-4" onClick={() => navigate('/organizations')}>
          Back to Organizations
        </Button>
      </div>
    );
  }

  const statusVariant =
    organization.status === 'ACTIVE'
      ? 'success'
      : organization.status === 'SUSPENDED'
        ? 'warning'
        : 'default';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/organizations')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
              Organization Details
            </h1>
            <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
              View and manage organization information
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            leftIcon={<Users className="h-4 w-4" />}
            asChild
          >
            <Link to={`/organizations/${id}/users`}>View Users</Link>
          </Button>
        </div>
      </div>

      {/* Organization Overview Card */}
      <Card>
        <CardBody className="p-0">
          {/* Top section: Icon + Name + Status */}
          <div className="flex items-center gap-5 p-6 pb-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Building2 className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-foreground dark:text-foreground-dark truncate">
                  {organization.name}
                </h2>
                <Badge variant={statusVariant} dot>
                  {organization.status}
                </Badge>
              </div>
              <p className="mt-0.5 text-sm text-foreground-muted dark:text-foreground-dark-muted">
                {organization.code}
              </p>
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-px border-t border-border dark:border-border-dark bg-border dark:bg-border-dark lg:grid-cols-4">
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Layers className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Type</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {organization.orgType}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Created</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                {formatDate(organization.addedDate)}
              </p>
            </div>
            <div className="bg-card dark:bg-card-dark p-4">
              <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                <Hash className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wide">Org ID</span>
              </div>
              <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark truncate" title={organization.id}>
                {organization.id}
              </p>
            </div>
            {organization.isCreatedByAdmin !== null && organization.isCreatedByAdmin !== undefined ? (
              <div className="bg-card dark:bg-card-dark p-4">
                <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                  <Shield className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase tracking-wide">Created By</span>
                </div>
                <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {organization.isCreatedByAdmin ? 'Admin' : 'Self-registered'}
                </p>
              </div>
            ) : (
              <div className="bg-card dark:bg-card-dark p-4">
                <div className="flex items-center gap-2 text-foreground-muted dark:text-foreground-dark-muted">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase tracking-wide">Updated</span>
                </div>
                <p className="mt-1.5 text-sm font-semibold text-foreground dark:text-foreground-dark">
                  {organization.updatedDated ? formatDate(organization.updatedDated) : 'N/A'}
                </p>
              </div>
            )}
          </div>

          {/* Configuration flags */}
          <div className="border-t border-border dark:border-border-dark px-6 py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-foreground-muted dark:text-foreground-dark-muted mb-3">
              Configuration
            </p>
            <div className="flex flex-wrap gap-2">
              <ConfigFlag label="Payroll Specific" enabled={organization.isPayrollSpecific} />
              <ConfigFlag label="Multiple Units" enabled={organization.isMultipleUnitsAllowed} />
              <ConfigFlag label="Default Contract Required" enabled={organization.isDefaultContractRequired} />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Subscription Plan */}
      <div className="grid gap-6">

        <Card>
          <CardHeader
            title="Subscription Plan"
            action={
              plan && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<History className="h-4 w-4" />}
                    onClick={() => setAuditOpen(true)}
                  >
                    Audit
                  </Button>
                  {canManagePlan && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<RefreshCw className="h-4 w-4" />}
                        onClick={() => navigate(`/organizations/${id}/renew`)}
                      >
                        Renew
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<ArrowUpCircle className="h-4 w-4" />}
                        onClick={() => navigate(`/organizations/${id}/upgrade`)}
                      >
                        Upgrade
                      </Button>
                    </>
                  )}
                </div>
              )
            }
          />
          <CardBody>
            {plan ? (
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-lg border border-border dark:border-border-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <span className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                        {plan.name}
                      </span>
                      <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                        {plan.planTermType}
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={plan.subscriptionPlanStatus === 'ACTIVE' ? 'success' : 'warning'}
                  >
                    {plan.subscriptionPlanStatus}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <div className="rounded-lg bg-secondary-50 dark:bg-secondary-900/30 p-4">
                    <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                      Max Employees
                    </p>
                    <p className="mt-1 text-2xl font-bold text-foreground dark:text-foreground-dark">
                      {plan.maxEmployees}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary-50 dark:bg-secondary-900/30 p-4">
                    <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                      Max Contracts
                    </p>
                    <p className="mt-1 text-2xl font-bold text-foreground dark:text-foreground-dark">
                      {plan.maxContracts}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary-50 dark:bg-secondary-900/30 p-4">
                    <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                      Active Contracts
                    </p>
                    <p className="mt-1 text-2xl font-bold text-foreground dark:text-foreground-dark">
                      {plan.maxActiveContracts}
                    </p>
                  </div>
                  <div className="rounded-lg bg-secondary-50 dark:bg-secondary-900/30 p-4">
                    <p className="text-xs font-medium text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wide">
                      Next Renewal
                    </p>
                    <p className="mt-1 text-lg font-bold text-foreground dark:text-foreground-dark">
                      {formatDate(plan.nextRenewDate)}
                    </p>
                  </div>
                </div>

                <div className="border-t border-border dark:border-border-dark pt-4">
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 text-sm">
                    <div>
                      <p className="text-foreground-muted dark:text-foreground-dark-muted">Subscribed Date</p>
                      <p className="mt-0.5 font-medium text-foreground dark:text-foreground-dark">
                        {formatDate(plan.subscribedDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-muted dark:text-foreground-dark-muted">Cycle Start</p>
                      <p className="mt-0.5 font-medium text-foreground dark:text-foreground-dark">
                        {formatDate(plan.planCycleStartDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-foreground-muted dark:text-foreground-dark-muted">Cycle End</p>
                      <p className="mt-0.5 font-medium text-foreground dark:text-foreground-dark">
                        {formatDate(plan.planCycleEndDate)}
                      </p>
                    </div>
                    {plan.maxUsers !== undefined && (
                      <div>
                        <p className="text-foreground-muted dark:text-foreground-dark-muted">Max Users</p>
                        <p className="mt-0.5 font-medium text-foreground dark:text-foreground-dark">
                          {plan.maxUsers}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <CreditCard className="mx-auto h-12 w-12 text-foreground-muted dark:text-foreground-dark-muted" />
                <p className="mt-2 text-foreground-muted dark:text-foreground-dark-muted">
                  No subscription plan assigned
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Audit Timeline */}
      {auditId && (
        <AuditTimeline
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          title={`${organization.name} Audit Logs`}
          fetchLogs={fetchAuditLogs}
        />
      )}
    </div>
  );
}

export default OrganizationDetailsPage;
